/**
 * The one place a realtime conversation event is handed from transport to the UI.
 *
 * The backend publishes `new_message` on the business socket
 * (api/conversation_routes.py -> webrtc_manager.broadcast_to_business). Web owned no consumer for
 * it at all: the frame reached useWebRTC, fell through its `switch` to
 * `console.log('Unknown WebRTC message type:', ...)`, and died there. The operator's thread
 * therefore only updated when some other action happened to refetch it — which is exactly the
 * "customer replied but Web shows nothing until I reply again" behaviour.
 *
 * WHY THIS IS A SIGNAL, NOT A MESSAGE STORE
 * db.messages is the durable authority (Bedrock Rule 2). A socket payload is delivery, never truth:
 * it can arrive twice, out of order, or not at all. So subscribers are handed the conversation id
 * and are expected to REFETCH the thread through the normal API. Nothing here is rendered directly,
 * and no message content is cached, so a dropped or duplicated frame costs at most one refetch.
 *
 * It is deliberately tiny and framework-free: one module, no second messaging authority, and no
 * dependency that would tempt a component to subscribe to the socket on its own.
 */

const subscribers = new Set();

/** Subscribe to conversation activity. Returns an unsubscribe function. */
export function onConversationActivity(handler) {
  if (typeof handler !== 'function') return () => {};
  subscribers.add(handler);
  return () => subscribers.delete(handler);
}

/**
 * Announce that a conversation changed. Called by the socket transport only.
 * Each subscriber is isolated: one throwing handler must not stop the others from refetching.
 */
export function emitConversationActivity(event) {
  if (!event || !event.conversationId) return 0;
  let delivered = 0;
  subscribers.forEach((handler) => {
    try {
      handler(event);
      delivered += 1;
    } catch (err) {
      // A rendering bug in one thread view must never silence live updates everywhere else.
      console.warn('[conversationRealtime] subscriber failed:', err);
    }
  });
  return delivered;
}

/** Test seam. Not used by application code. */
export function _subscriberCount() {
  return subscribers.size;
}
